import { useEffect, useRef, useState } from "react";
import type { ProductFamily } from "../domain/productWorkflow";
import { catalogRequest } from "../domain/catalogApi";
import { usePlannerStore } from "../store/usePlannerStore";
import { applyCatalogModel, type CatalogModelDetail } from "../store/catalogApply";
import "./ModelCatalog.css";

type Model = {id: string; name: string; description: string; category: string; origin?: "builtin" | "project"; width: number; height: number; depth: number; coverUrl: string};
type Filter = "all" | "wardrobe" | "shelving" | "chest";
const FILTERS: [Filter, string][] = [["all", "Все"], ["wardrobe", "Шкафы"], ["shelving", "Стеллажи"], ["chest", "Комоды"]];
/** Внутри раздела витрина сразу показывает его модели, на общем экране — все. */
const filterFor = (family: ProductFamily | null | undefined): Filter => family ?? "all";

export function ModelCatalog({ onBack, onEdit, onApplied, variant = "panel", family }: {
  onBack?: () => void;
  onEdit?: () => void;
  /** Сообщает раздел выбранной модели, чтобы открыть его шаги. */
  onApplied?: (family: ProductFamily) => void;
  variant?: "panel" | "showcase";
  family?: ProductFamily | null;
}) {
  const [models, setModels] = useState<Model[]>([]);
  const [pending, setPending] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilterState] = useState<Filter>(filterFor(family));
  // Смена фильтра не укорачивает страницу рывком: сетка держит прежнюю высоту,
  // иначе браузер подтягивал страницу вверх (приёмка 04.10, cat-01).
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridMinHeight, setGridMinHeight] = useState(0);
  const setFilter = (next: Filter) => {
    setGridMinHeight(height => Math.max(height, gridRef.current?.offsetHeight ?? 0));
    setFilterState(next);
  };
  useEffect(() => { setFilterState(filterFor(family)); setGridMinHeight(0); }, [family]);
  const project = usePlannerStore(state => state.project);
  const refresh = async () => {
    setLoading(true); setError("");
    try { setModels((await catalogRequest<{models: Model[]}>("/catalog")).models); }
    catch (e) { setError(e instanceof Error ? e.message : "Не удалось загрузить модели"); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);
  return <section className={`${variant === "panel" ? "panel-section " : ""}model-catalog model-catalog--${variant}`}>
    {onBack && <button className="secondary-button" onClick={onBack}>← Назад</button>}
    <div className="model-catalog-heading">
      <span className="eyebrow">Готовые изделия Egel</span>
      <h3>{variant === "showcase" ? "Начните с готовой модели" : "Витрина моделей"}</h3>
      <p className="sl-help">Выберите близкую конструкцию и измените размеры, наполнение и отделку под себя. В одном проекте остаётся одно изделие, поэтому новая основа заменит текущую.</p>
    </div>
    <div className="model-catalog-filters">{FILTERS.map(([value, title]) => <button key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}>{title}</button>)}</div>
    {error && <p className="workspace-error" role="alert">{error}</p>}
    {message && <div className="catalog-added" role="status"><strong>{message}</strong><p>Основа уже в конструкторе — все её размеры и наполнение можно менять.</p>{onEdit && <button className="primary-button" onClick={onEdit}>Настроить модель →</button>}</div>}
    {loading ? <p role="status">Загружаю готовые модели…</p> : <>
      {!models.length && !error && <div className="empty-state"><strong>Витрина недоступна</strong><p>Не удалось получить список моделей. Изделие можно собрать с нуля.</p></div>}
      {models.length > 0 && !models.some(m => filter === "all" || m.category === filter) && <div className="empty-state"><strong>В этом разделе пока нет моделей</strong><p>Их добавляют дизайнеры Egel. Посмотрите другие разделы или соберите изделие с нуля.</p></div>}
      <div className="model-catalog-grid" ref={gridRef} style={gridMinHeight ? { minHeight: gridMinHeight } : undefined}>{models.filter(m => filter === "all" || m.category === filter).map(m => <article key={m.id} className={pending?.id === m.id ? "is-pending" : undefined}>
        <button className="model-catalog-card" aria-pressed={pending?.id === m.id} onClick={() => {setPending(m); setMessage(""); setError("");}}>
          <img src={m.coverUrl} loading="lazy" alt={m.name} /><strong>{m.name}</strong><small>{m.width} × {m.height} × {m.depth} мм{m.origin === "builtin" ? " · модель Egel" : ""}</small>
        </button>
        {pending?.id === m.id && <div className="catalog-model-detail"><p>{m.description || "Готовая конструкция с возможностью изменить размеры, наполнение и отделку."}</p><button className="primary-button" disabled={adding} onClick={async () => {
          if (project.modules.length && !window.confirm("Готовая основа заменит текущее изделие в этом проекте. Прежний вариант можно вернуть кнопкой «Отменить». Продолжить?")) return;
          setAdding(true); setError("");
          try {
            const {model} = await catalogRequest<{model: CatalogModelDetail}>(`/catalog/${m.id}`);
            const applied = applyCatalogModel({...model, category: m.category});
            setMessage(`«${model.name}» выбрана как основа`); setPending(null);
            onApplied?.(applied);
          } catch (e) { setError(e instanceof Error ? e.message : "Не удалось добавить модель"); }
          finally { setAdding(false); }
        }}>{adding ? "Готовлю модель…" : project.modules.length ? "Заменить текущее изделие" : "Взять за основу"}</button></div>}
      </article>)}</div>
      <button className="secondary-button" disabled={adding} onClick={() => void refresh()}>Обновить список моделей</button>
    </>}
  </section>;
}
